import { attachmentApi, handleError } from '@ardoq/api';
import { ArdoqId } from '@ardoq/api-types';
import {
  action$,
  collectRoutines,
  dispatchAction,
  ofType,
  reducedStream,
  reducer,
} from '@ardoq/rxbeach';
import { logError } from '@ardoq/logging';
import { ArdoqError } from '@ardoq/common-helpers';
import { map, switchMap, tap, withLatestFrom } from 'rxjs';
import { documentArchiveAttachments$ } from '../../streams/documentArchive/documentArchiveAttachments$';
import { requestDeletion } from '../actions';
import {
  closeDuplicatesModal,
  duplicatesLoaded,
  duplicatesLoadFailed,
  findDuplicates,
  noDuplicatesFound,
  toggleDuplicateSelected,
} from './actions';
import { DuplicateGroup, toDuplicateGroups } from './toDuplicateGroups';

export type DuplicatesState =
  | { status: 'closed' }
  | { status: 'loading' }
  | {
      status: 'loaded';
      groups: DuplicateGroup[];
      selectedIds: ArdoqId[];
    };

const CLOSED: DuplicatesState = { status: 'closed' };

const toRemovableIds = (groups: DuplicateGroup[]): ArdoqId[] =>
  groups.flatMap(group =>
    group.attachments
      .filter(attachment => attachment.status === 'remove')
      .map(attachment => attachment._id)
  );

const handleFindDuplicates = (): DuplicatesState => ({ status: 'loading' });

const handleDuplicatesLoaded = (
  _state: DuplicatesState,
  groups: DuplicateGroup[]
): DuplicatesState => ({
  status: 'loaded',
  groups,
  selectedIds: toRemovableIds(groups),
});

const handleToggleDuplicateSelected = (
  state: DuplicatesState,
  id: ArdoqId
): DuplicatesState => {
  if (state.status !== 'loaded') {
    return state;
  }
  if (!toRemovableIds(state.groups).includes(id)) {
    return state;
  }
  return {
    ...state,
    selectedIds: state.selectedIds.includes(id)
      ? state.selectedIds.filter(selectedId => selectedId !== id)
      : [...state.selectedIds, id],
  };
};

const close = (): DuplicatesState => CLOSED;

export const duplicates$ = reducedStream<DuplicatesState>(
  'documentArchiveDuplicates$',
  CLOSED,
  [
    reducer(findDuplicates, handleFindDuplicates),
    reducer(duplicatesLoaded, handleDuplicatesLoaded),
    reducer(duplicatesLoadFailed, close),
    reducer(toggleDuplicateSelected, handleToggleDuplicateSelected),
    reducer(closeDuplicatesModal, close),
    reducer(noDuplicatesFound, close),
    reducer(requestDeletion, close),
  ]
);

const handleFindDuplicatesFailed = (error: ArdoqError): void => {
  logError(error);
  dispatchAction(duplicatesLoadFailed());
};

const handleDuplicatesResult = (groups: DuplicateGroup[]): void => {
  if (groups.length === 0) {
    dispatchAction(noDuplicatesFound());
    return;
  }
  dispatchAction(duplicatesLoaded(groups));
};

const handleFindDuplicatesRoutine = action$.pipe(
  ofType(findDuplicates),
  switchMap(attachmentApi.getDuplicates),
  handleError(handleFindDuplicatesFailed),
  withLatestFrom(documentArchiveAttachments$),
  map(([groups, { models }]) => toDuplicateGroups(groups, models)),
  tap(handleDuplicatesResult)
);

export const duplicatesRoutines = collectRoutines(handleFindDuplicatesRoutine);
